/**
 * Terms & Conditions page — /terms
 *
 * Content drafted by founder, sourced against nikshala.com for entity name,
 * refund policy, jurisdiction, and support contact (2026-07-27). Marked as
 * Published as final (2026-07-28) per founder decision.
 *
 * Rendered on the marketing shell (public route, no auth required) so
 * prospective and current users can read it before signing up.
 */
import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="terms-page">
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Ministry of Exchange</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-ink">Terms &amp; Conditions</h1>
        <p className="mt-3 text-sm text-muted" data-testid="terms-last-updated">
          Last updated: 2 August 2026 · third-party links (travel search) clarified
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-ink/85 [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-ink [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:ml-5 [&_ul]:list-disc [&_li]:mb-1">

          <section>
            <h2>1. Acceptance of these Terms</h2>
            <p>
              By creating an account on Ministry of Exchange (&quot;the Platform,&quot; &quot;we,&quot; &quot;us&quot;),
              operated by Nikshala AI (the parent company, with Ministry of Exchange operating as a sister
              product under the Nikshala AI family), you agree to these Terms &amp; Conditions and our
              {" "}<Link to="/privacy" className="text-elec font-semibold underline">Privacy Policy</Link>.
              If you do not agree, do not create an account or use the Platform.
            </p>
          </section>

          <section>
            <h2>2. What Ministry of Exchange Is</h2>
            <p>Ministry of Exchange is an informational and coordination platform for students on exchange semesters or internships abroad. It provides:</p>
            <ul>
              <li><b>Northstar</b> — visa and relocation timeline information, sourced from official government sources where available.</li>
              <li><b>Cohort</b> — peer discovery and matching with other students headed to the same host country, city, or university.</li>
              <li><b>Assembly</b> — tools to coordinate trips, arrivals, roommate searches, and social plans with your Cohort.</li>
              <li><b>Drift</b> — informational weekend travel discovery.</li>
              <li><b>Smart Packing</b> — a personalised packing checklist.</li>
              <li><b>Concierge</b> — optional, paid, human-assisted visa support.</li>
            </ul>
          </section>

          <section>
            <h2>3. Eligibility</h2>
            <p>
              You must be at least 18 years old and a genuine student or recent graduate planning or undertaking
              an exchange semester, internship, or related study-abroad program to use this Platform. You must
              provide accurate information when creating your account.
            </p>
          </section>

          <section>
            <h2>4. Your Account</h2>
            <p>
              You are responsible for maintaining the confidentiality of your login credentials and for all
              activity under your account. Notify us immediately if you suspect unauthorized use of your account.
              You may delete your account at any time (see Section 10).
            </p>
          </section>

          <section>
            <h2>5. Visa &amp; Immigration Information — Important Disclaimer</h2>
            <p>
              Information on this Platform, including visa fees, deadlines, and requirements, is provided for
              general informational purposes only. It is not legal or immigration advice, and it is not a
              substitute for consulting the relevant embassy, consulate, or a licensed immigration professional.
            </p>
            <ul>
              <li>We source visa information from official government sources where possible and note when it is not, but requirements change and errors are possible.</li>
              <li>No visa outcome is ever guaranteed. We are not responsible for visa denials, delays, or any consequence arising from reliance on information provided on the Platform.</li>
              <li>Always independently verify current requirements with the relevant consulate or embassy before taking any action.</li>
            </ul>
          </section>

          <section>
            <h2>6. Cohort, Assembly, and Peer Connections</h2>
            <ul>
              <li>The Platform facilitates connections between students who opt in to be visible to their cohort. We do not conduct background checks on users and cannot guarantee the identity, intentions, or conduct of any other user.</li>
              <li>You interact with other users, and meet in person, entirely at your own risk.</li>
              <li>Assembly coordination on the Platform is intent-only: we do not process, hold, or pool money between users, and we do not share physical addresses through the app.</li>
              <li>We reserve the right to remove any user or content that violates these Terms.</li>
            </ul>
          </section>

          <section>
            <h2>7. Concierge (Paid Service)</h2>
            <ul>
              <li>Concierge is an optional, separately-paid service providing human-assisted visa support. Current pricing is displayed on the Platform at time of booking.</li>
              <li>Booking is currently coordinated via calendar scheduling, not automated in-app payment.</li>
              <li>Concierge assistance does not guarantee any visa or immigration outcome.</li>
            </ul>
            <p className="mt-3 font-semibold text-ink">Refund policy (consistent with Nikshala's standing policy for personalized services):</p>
            <ul>
              <li><b>Full refund:</b> if we are unable to deliver the service due to circumstances on our end.</li>
              <li><b>Partial refund (up to 50%):</b> if you cancel before work on your case has begun.</li>
              <li><b>No refund:</b> once work on your case has started, given the personalized, time-intensive nature of the service.</li>
              <li>To cancel, contact us in writing at <a href="mailto:nd@nikshala.com" className="text-elec font-semibold underline">nd@nikshala.com</a>. Cancellation requests are processed within 5-6 business days.</li>
            </ul>
          </section>

          <section>
            <h2>8. Third-Party Links and Partners</h2>
            <p>
              The Platform links to third-party services for travel search (e.g. flight and bus search
              providers such as Ryanair, FlixBus, and Omio) and may link to other external sites.
              We are not responsible for the privacy practices, security, or content of these external
              sites. Once you leave our Platform via one of these links, their own privacy policy and
              terms apply, not ours. Where we have a commercial affiliate relationship, we will
              disclose this clearly.
            </p>
          </section>

          <section>
            <h2>9. Prohibited Conduct</h2>
            <p>You agree not to: provide false information; harass, threaten, or impersonate other users; use the Platform unlawfully; attempt to circumvent security or scrape data; use another user's account without permission.</p>
          </section>

          <section>
            <h2>10. Data, Privacy, and Account Deletion</h2>
            <p>
              Our collection and use of your personal data is governed by our separate
              {" "}<Link to="/privacy" className="text-elec font-semibold underline">Privacy Policy</Link>.
              You may delete your account at any time. Self-deletion is soft (14-day grace period, reversible)
              before permanent deletion. We do not sell your personal data. EU/EEA users have GDPR rights
              including access, correction, deletion, and portability.
            </p>
          </section>

          <section>
            <h2>11. Intellectual Property</h2>
            <p>
              The Platform's design, content, and underlying software are owned by Nikshala AI. You retain
              ownership of content you post but grant us a license to display it as necessary for the Platform to
              function.
            </p>
          </section>

          <section>
            <h2>12. Disclaimers and Limitation of Liability</h2>
            <p>
              The Platform is provided &quot;as is.&quot; We do not guarantee accuracy of information. We are not
              liable for indirect or consequential damages arising from use of the Platform. Nothing excludes
              liability that cannot be excluded under applicable law.
            </p>
          </section>

          <section>
            <h2>13. Termination</h2>
            <p>
              We may suspend or terminate accounts that violate these Terms. You may stop using the Platform and
              delete your account at any time.
            </p>
          </section>

          <section>
            <h2>14. Changes to These Terms</h2>
            <p>
              We may update these Terms. We will make reasonable efforts to notify active users of material changes.
            </p>
          </section>

          <section>
            <h2>15. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of India. You submit to the
              exclusive jurisdiction of the courts in India for the resolution of any disputes, consistent with
              Nikshala's standing policy across its other products.
            </p>
          </section>

          <section>
            <h2>16. Contact</h2>
            <p>
              Questions about these Terms:
              {" "}<a href="mailto:nd@nikshala.com" className="text-elec font-semibold underline">nd@nikshala.com</a>
              {" "}or <a href="tel:+918263907201" className="text-elec font-semibold underline">+91 82639 07201</a>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
